import { matchPath } from "react-router-dom";

/**
 * The routes that need an account.
 *
 * This is the one list. It exists so that *navigation* can be gated before it
 * happens: a signed-out visitor who clicks "My Topics" should get the modal,
 * not a trip to `/my-topics` where an inert copy of the page sits behind it.
 *
 * `RequireAuth` remains the authority on the routes themselves — it wraps each
 * of these in `App.tsx` and is what catches a direct load (a pasted URL, a
 * bookmark, a link from outside the app). Nothing here replaces it; this only
 * lets the app decline to walk into a wall it can see coming.
 *
 * The two must agree. If you add a `RequireAuth` route, add it here too — the
 * cost of forgetting is that clicking into it navigates and *then* shows the
 * gate, which is exactly the old behaviour, not a broken page.
 */
export const AUTH_REQUIRED_PATHS = [
  /* My Topics and everything under it: ai, progress, time, speaking, edit/:id */
  "/my-topics",
  "/my-topics/*",
  /* My Vocabulary and its tabs, collections and practice. `/vocabulary` itself
     is the only one that is not a prefix of another entry, so it needs its own
     row — `/*` does not match the bare path. */
  "/vocabulary",
  "/vocabulary/*",
  /* `/session/:title` is public; only the practice surface inside it is not. */
  "/session/:title/practice",
  "/call/:roomId",
  /* The group *list* is public; a group's own page is not. */
  "/study-groups/:id",
] as const;

/**
 * Does this destination need an account?
 *
 * Query and hash are stripped first: these are called with the exact string a
 * caller is about to navigate to, and `/vocabulary/practice?mode=due` is the
 * same destination as `/vocabulary/practice`.
 */
export const isAuthRequiredPath = (to: string): boolean => {
  const pathname = to.split(/[?#]/)[0];

  return AUTH_REQUIRED_PATHS.some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
};
